import { Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { useState, useEffect, createContext, useContext } from 'react';
import { api } from './api/client';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  BarChart3,
  LogOut,
  ChevronDown,
  Mail,
  MailCheck,
  Settings2,
  Coins,
  Loader2,
  Square,
  ScrollText,
  Database,
  Activity,
  Ban,
  FileSpreadsheet,
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';

import Login from './pages/Login';
import Stats from './pages/Stats';
import Settings from './pages/Settings';
import Verify from './pages/Verify';
import ApiLogsPage from './pages/ApiLogs';
import EmailExplorer from './pages/EmailExplorer';
import EmailPage from './pages/EmailPage';
import EventsExplorer from './pages/EventsExplorer';
import Blocklist from './pages/Blocklist';
import Exports from './pages/Exports';
import GlobalSearch from './components/GlobalSearch';

interface User {
  id: string;
  email: string;
  role: string;
  timezone: string;
}

export interface ActiveBatch {
  id: string;
  projectName: string;
  status: string;
  totalCount: number;
  processedCount: number;
  cacheHits: number;
  createdAt: string;
  progress: number;
}

interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  loading: boolean;
}

interface BatchContextType {
  activeBatches: ActiveBatch[];
  fetchActiveBatches: () => Promise<void>;
  addOptimisticBatch: (batch: ActiveBatch) => void;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  setUser: () => {},
  loading: true,
});

export const BatchContext = createContext<BatchContextType>({
  activeBatches: [],
  fetchActiveBatches: async () => {},
  addOptimisticBatch: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function useBatches() {
  return useContext(BatchContext);
}

const navItems = [
  { path: '/', label: 'Email Explorer', icon: Database },
  { path: '/events', label: 'Events', icon: Activity },
  { path: '/stats', label: 'Statistics', icon: BarChart3 },
  { path: '/verify', label: 'Verify', icon: MailCheck },
  { path: '/blocklist', label: 'Blocklist', icon: Ban },
  { path: '/exports', label: 'Exports', icon: FileSpreadsheet },
  { path: '/api-logs', label: 'API Logs', icon: ScrollText },
  { path: '/settings', label: 'Settings', icon: Settings2 },
];

function Sidebar() {
  const { user, setUser } = useAuth();
  const { activeBatches } = useBatches();
  const location = useLocation();
  const [credits, setCredits] = useState<number | null>(null);
  const [stoppingBatchId, setStoppingBatchId] = useState<string | null>(null);

  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const data = await api.getCredits();
        setCredits(data.credits);
      } catch {
        // Silently fail - credits will show as null
      }
    };
    fetchCredits();
  }, []);

  const handleStopBatch = async (batchId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setStoppingBatchId(batchId);
    try {
      await api.stopBatch(batchId);
    } catch {
      // Silently fail
    } finally {
      setStoppingBatchId(null);
    }
  };

  const handleLogout = () => {
    api.logout();
    setUser(null);
  };

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 border-r bg-card flex flex-col">
      <div className="p-6">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Mail className="h-4 w-4" />
          </div>
          <div>
            <h1 className="font-semibold">Email Core</h1>
            {credits !== null && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Coins className="h-3 w-3" />
                <span>{credits.toLocaleString()} credits</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <GlobalSearch />

      <Separator />

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </Link>
          );
        })}

        {/* Active Batches */}
        {activeBatches.length > 0 && (
          <>
            <Separator className="my-3" />
            <div className="px-3 py-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                <Loader2 className="h-3 w-3 animate-spin" />
                Active Tasks
              </div>
              <div className="space-y-2">
                {activeBatches.map((batch) => (
                  <Link
                    key={batch.id}
                    to="/verify?tab=history"
                    className="block rounded-lg border bg-background p-2 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium truncate flex-1">
                        {batch.projectName}
                      </span>
                      <button
                        onClick={(e) => handleStopBatch(batch.id, e)}
                        disabled={stoppingBatchId === batch.id}
                        className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                      >
                        {stoppingBatchId === batch.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Square className="h-3 w-3" />
                        )}
                      </button>
                    </div>
                    <Progress value={batch.progress} className="h-1.5" />
                    <div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
                      <span>{batch.progress}%</span>
                      <span>{batch.processedCount}/{batch.totalCount}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
      </nav>

      <Separator />

      <div className="p-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-between">
              <span className="truncate text-sm">{user?.email}</span>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Sidebar />
      <main className="ml-64 min-h-screen bg-muted/30 p-8">{children}</main>
    </>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeBatches, setActiveBatches] = useState<ActiveBatch[]>([]);

  const fetchActiveBatches = async () => {
    try {
      const { batches } = await api.getActiveBatches();
      setActiveBatches(batches);
    } catch {
      // Silently fail - batches will remain empty
    }
  };

  const addOptimisticBatch = (batch: ActiveBatch) => {
    setActiveBatches((prev) => [batch, ...prev]);
  };

  useEffect(() => {
    const checkAuth = async () => {
      if (api.getToken()) {
        try {
          const userData = await api.getMe();
          setUser(userData);
        } catch {
          api.logout();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  // Polling for active batches
  useEffect(() => {
    if (!user) return;
    fetchActiveBatches();
    const interval = setInterval(fetchActiveBatches, 5000);
    return () => clearInterval(interval);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading }}>
      <BatchContext.Provider value={{ activeBatches, fetchActiveBatches, addOptimisticBatch }}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <EmailExplorer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/events"
          element={
            <ProtectedRoute>
              <EventsExplorer />
            </ProtectedRoute>
          }
        />
        <Route path="/projects" element={<Navigate to="/settings?tab=projects" replace />} />
        <Route path="/api-keys" element={<Navigate to="/settings?tab=api-keys" replace />} />
        <Route path="/users" element={<Navigate to="/settings?tab=users" replace />} />
        <Route
          path="/stats"
          element={
            <ProtectedRoute>
              <Stats />
            </ProtectedRoute>
          }
        />
        <Route
          path="/verify"
          element={
            <ProtectedRoute>
              <Verify />
            </ProtectedRoute>
          }
        />
        <Route path="/emails" element={<Navigate to="/" replace />} />
        <Route
          path="/emails/:email"
          element={
            <ProtectedRoute>
              <EmailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/blocklist"
          element={
            <ProtectedRoute>
              <Blocklist />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exports"
          element={
            <ProtectedRoute>
              <Exports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/api-logs"
          element={
            <ProtectedRoute>
              <ApiLogsPage />
            </ProtectedRoute>
          }
        />
      </Routes>
      </BatchContext.Provider>
    </AuthContext.Provider>
  );
}
